import React, { useState, useEffect, useRef, useMemo } from 'react';
import FormField from './FormField';
import { Button } from '../Button2';
import { Icon } from '../Icon';
import styles from './FormModal.module.css';

/**
 * Generic form modal for "add X" / "edit X" workflows.
 *
 * Consumers describe their form via a declarative `fields` array;
 * FormModal handles state, validation, error display, submit lifecycle,
 * spinner, autofocus, Escape, backdrop click, reduced-motion, and the
 * half-width 2-column grid layout.
 *
 * Ported verbatim from Themis (`cybpass-themis-agent/frontend/src/
 * components/ui/FormModal/FormModal.jsx`) in Epic 11.X. The JSX logic
 * is byte-for-byte identical; only the CSS layer was retargeted onto
 * Athena's design tokens. Keeping the JSX verbatim makes it cheap to
 * pull future Themis upstream changes.
 *
 * Props:
 *   title             — header text
 *   headerExtra       — optional node rendered below the title (e.g. a "How to get credentials?" link)
 *   fields            — array of field configs (see FormField.jsx for the catalog).
 *                       Each field may declare an optional `visible(allValues)`
 *                       callback; fields where it returns `false` are hidden
 *                       from the DOM, skipped by required validation, and
 *                       skipped by autofocus selection. Their last-known
 *                       value stays in state so toggling visibility back on
 *                       restores it. A field may also declare
 *                       `afterChange(value, { values, setValue })` — runs when
 *                       its value changes so it can write derived values into
 *                       other fields in the same update (e.g. auto-suggest an
 *                       ID when the controlling select changes).
 *   initialValues     — optional pre-fill, overrides per-field defaultValue
 *   submitLabel       — primary button label (default "Submit")
 *   cancelLabel       — secondary button label (default "Cancel")
 *   submittingLabel   — primary button label while in-flight (default "Submitting...")
 *   onSubmit          — (values) => void | Promise; rejection message becomes the inline error
 *   onValuesChange    — optional (values) => void; fired after every field
 *                       change so a parent can react (e.g. refetch dynamic
 *                       options when a controlling select changes)
 *   onClose           — called on backdrop / Escape / Cancel / X
 *   closeOnBackdrop   — default true
 *   closeOnEscape     — default true
 *   maxWidth          — number in px (default 600)
 *   className         — extra wrapper class for one-off layout
 *   children          — optional custom content rendered after the
 *                       declarative fields and before the submit error +
 *                       action buttons. Escape hatch for forms that mix
 *                       declarative fields with a bespoke interactive
 *                       sub-section (e.g. the control-implementation
 *                       evidence collector) that can't be expressed as a
 *                       FormField. Children are inside the <form>, so they
 *                       participate in submit/Enter; manage their own
 *                       state in the consumer and read it in onSubmit.
 */
const FormModal = ({
  title,
  headerExtra = null,
  fields = [],
  initialValues = {},
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  submittingLabel = 'Submitting...',
  onSubmit,
  onValuesChange,
  onClose,
  closeOnBackdrop = true,
  closeOnEscape = true,
  maxWidth = 600,
  className = '',
  children = null,
}) => {
  // Build the initial values dict by walking the schema once. defaultValue
  // from the field config is the fallback; initialValues overrides per key.
  const buildInitial = () => {
    const out = {};
    fields.forEach((field) => {
      if (field.type === 'display' || field.type === 'custom') return; // not value-bearing
      if (Object.prototype.hasOwnProperty.call(initialValues, field.name)) {
        out[field.name] = initialValues[field.name];
      } else if (field.defaultValue !== undefined) {
        out[field.name] = field.defaultValue;
      } else {
        // Sensible empty default per type so React inputs stay controlled
        switch (field.type) {
          case 'multi-select':
          case 'checkbox-group':
          case 'files':
            out[field.name] = [];
            break;
          case 'checkbox':
          case 'toggle':
            out[field.name] = false;
            break;
          case 'file':
            out[field.name] = null;
            break;
          case 'number':
            out[field.name] = '';
            break;
          default:
            out[field.name] = '';
        }
      }
    });
    return out;
  };

  const [values, setValues] = useState(buildInitial);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Resolve which fields are visible right now. Hidden fields keep their
  // value in state but skip rendering, autofocus targeting, and required
  // validation. The visible() callback receives the full current values
  // dict so a field can branch on any other field.
  const visibleFields = useMemo(
    () => fields.filter((f) => (typeof f.visible === 'function' ? f.visible(values) : true)),
    [fields, values],
  );

  // Find the first visible field marked autoFocus (or fall back to the
  // first visible value-bearing input). Used by FormField via the
  // autoFocusRef forward.
  const autoFocusFieldName = useMemo(() => {
    const focusable = (f) => f.type !== 'display' && f.type !== 'custom';
    const explicit = visibleFields.find((f) => f.autoFocus && focusable(f));
    if (explicit) return explicit.name;
    const first = visibleFields.find(focusable);
    return first?.name;
  }, [visibleFields]);
  const autoFocusRef = useRef(null);

  useEffect(() => {
    autoFocusRef.current?.focus();
    // Only run on mount; consumers don't expect autofocus to chase config changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!closeOnEscape) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [closeOnEscape, onClose]);

  const handleBackdropClick = (e) => {
    if (closeOnBackdrop && e.target === e.currentTarget) onClose?.();
  };

  const handleFieldChange = (name, value) => {
    setValues((prev) => {
      const next = { ...prev, [name]: value };
      // Per-field afterChange hook — lets a controlling field write derived
      // values into the same update (e.g. auto-suggest an ID when a select
      // changes). setValue mutates `next` before it lands, so dependent
      // fields re-render with the derived value in one pass.
      const fieldDef = fields.find((f) => f.name === name);
      fieldDef?.afterChange?.(value, {
        values: next,
        setValue: (depName, depValue) => {
          next[depName] = depValue;
        },
      });
      onValuesChange?.(next);
      return next;
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Run built-in required + custom validators across visible fields only.
  // Hidden fields skip validation — they're not part of the user's
  // current input surface, so requiring them would be impossible to satisfy.
  const runValidation = () => {
    const out = {};
    visibleFields.forEach((field) => {
      if (field.type === 'display' || field.type === 'custom') return;
      const value = values[field.name];

      if (field.required) {
        const empty =
          value === null ||
          value === undefined ||
          value === '' ||
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === 'boolean' && value === false && field.type !== 'toggle' && field.type !== 'checkbox');
        // For checkbox / toggle, "required" means must be checked = true
        const requiredCheckboxFailed =
          (field.type === 'checkbox' || field.type === 'toggle') && value !== true;
        if (empty || requiredCheckboxFailed) {
          out[field.name] = `${field.label} is required`;
          return;
        }
      }

      if (field.validate) {
        const customError = field.validate(value, values);
        if (customError) out[field.name] = customError;
      }
    });
    return out;
  };

  // Apply per-field transforms (e.g., split textarea on \n) just before
  // calling onSubmit, so consumers receive the shape they expect.
  const applyTransforms = () => {
    const out = {};
    fields.forEach((field) => {
      if (field.type === 'display' || field.type === 'custom') return;
      const raw = values[field.name];
      out[field.name] = field.transform ? field.transform(raw) : raw;
    });
    return out;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    const validationErrors = runValidation();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const submitValues = applyTransforms();
      await onSubmit?.(submitValues);
      onClose?.();
    } catch (err) {
      setSubmitError(
        err?.message ?? err?.toString?.() ?? 'Submission failed. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Group adjacent half-width fields into rows so the AddControlModal
  // "Category half-width on top" pattern keeps working. Walks the
  // currently-visible field list so a half-width field that gets
  // hidden doesn't leave an orphaned grid cell.
  const renderedRows = useMemo(() => {
    const rows = [];
    let halfBuffer = [];
    visibleFields.forEach((field, idx) => {
      if (field.width === 'half') {
        halfBuffer.push(field);
        if (halfBuffer.length === 2 || idx === visibleFields.length - 1) {
          rows.push({ kind: 'half-row', fields: halfBuffer });
          halfBuffer = [];
        }
      } else {
        if (halfBuffer.length > 0) {
          rows.push({ kind: 'half-row', fields: halfBuffer });
          halfBuffer = [];
        }
        rows.push({ kind: 'full', field });
      }
    });
    return rows;
  }, [visibleFields]);

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div
        className={`${styles.modalContainer} ${className}`.trim()}
        style={{ maxWidth: `${maxWidth}px` }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="form-modal-title"
      >
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderLeft}>
            <h2 id="form-modal-title" className={styles.modalTitle}>
              {title}
            </h2>
            {headerExtra && <div className={styles.headerExtra}>{headerExtra}</div>}
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            aria-label="Close"
          >
            <Icon name="close" />
          </Button>
        </div>

        <form className={styles.modalForm} onSubmit={handleSubmit} noValidate>
          {renderedRows.map((row, rowIdx) => {
            if (row.kind === 'half-row') {
              return (
                <div key={`half-${rowIdx}`} className={styles.formGrid}>
                  {row.fields.map((field) => (
                    <FormField
                      key={field.name}
                      field={field}
                      value={values[field.name]}
                      error={errors[field.name]}
                      onChange={(v) => handleFieldChange(field.name, v)}
                      allValues={values}
                      setValue={handleFieldChange}
                      autoFocusRef={
                        field.name === autoFocusFieldName ? autoFocusRef : undefined
                      }
                    />
                  ))}
                </div>
              );
            }
            const { field } = row;
            return (
              <FormField
                key={field.name}
                field={field}
                value={values[field.name]}
                error={errors[field.name]}
                onChange={(v) => handleFieldChange(field.name, v)}
                allValues={values}
                setValue={handleFieldChange}
                autoFocusRef={
                  field.name === autoFocusFieldName ? autoFocusRef : undefined
                }
              />
            );
          })}

          {children}

          {submitError && <div className={styles.submitError}>{submitError}</div>}

          <div className={styles.modalActions}>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {cancelLabel}
            </Button>
            <Button type="submit" variant="default" loading={isSubmitting}>
              {isSubmitting ? submittingLabel : submitLabel}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormModal;
